import * as React from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Breadcrumbs,
    Button,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import type { Category } from "../../models/Category";
import { useErrorHandler, isNullOrEmpty } from "../../hooks/useErrorHandler";
import ErrorSummary from "../ErrorHandler/ErrorSummary";
import { buildTree, flattenWithLevel, formatIndentedLabel, getPath, isSameOrDescendant } from "./categoryTree.utils";
import { getApiErrorMessage } from "./apiError.utils";
import type { CategoryInput } from "./useCategoriesAdmin";

export type FormTarget = { mode: "create"; parentId: number | null } | { mode: "edit"; id: number };

type CategoryDetailsPanelProps = {
    target: FormTarget | null;
    categories: Category[];
    onCreate: (input: CategoryInput) => Promise<Category>;
    onUpdate: (id: number, input: CategoryInput) => Promise<void>;
    onRequestDelete: (id: number) => void;
    onCancel: () => void;
    onSaved: (categoryId: number) => void;
    onAddChild: (parentId: number) => void;
};

export default function CategoryDetailsPanel({
    target,
    categories,
    onCreate,
    onUpdate,
    onRequestDelete,
    onCancel,
    onSaved,
    onAddChild,
}: CategoryDetailsPanelProps) {
    const { errors, handleError, clearErrors } = useErrorHandler();
    const [categoryName, setCategoryName] = React.useState("");
    const [parentId, setParentId] = React.useState<number | null>(null);
    const [saving, setSaving] = React.useState(false);

    const editingCategory = target?.mode === "edit" ? categories.find((c) => c.id === target.id) : undefined;
    const isSystem = editingCategory?.clientId == null && target?.mode === "edit";

    React.useEffect(() => {
        clearErrors();
        if (!target) {
            setCategoryName("");
            setParentId(null);
        } else if (target.mode === "create") {
            setCategoryName("");
            setParentId(target.parentId);
        } else if (editingCategory) {
            setCategoryName(editingCategory.name);
            setParentId(editingCategory.parentId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target?.mode, target?.mode === "edit" ? target.id : target?.parentId]);

    const parentOptions = React.useMemo(() => {
        const flat = flattenWithLevel(buildTree(categories));
        if (target?.mode !== "edit") return flat;
        return flat.filter((o) => !isSameOrDescendant(categories, target.id, o.id));
    }, [categories, target]);

    const breadcrumbTrail = React.useMemo(() => {
        if (!target) return [];
        const anchorId = target.mode === "edit" ? target.id : target.parentId;
        return getPath(categories, anchorId ?? null);
    }, [target, categories]);

    if (!target) {
        return (
            <Paper variant="outlined" sx={{ p: 4, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary" align="center">
                    Select a category on the left to edit it, or use the + buttons to add a new one.
                </Typography>
            </Paper>
        );
    }

    const isEditing = target.mode === "edit";
    // `target.id` only exists on the "edit" branch of the union; TS can't
    // narrow that through the separately-computed `isEditing`/`isSystem`
    // booleans below, so pull it out once here where `target.mode === "edit"`
    // is checked directly.
    const editingId = target.mode === "edit" ? target.id : null;

    const handleSave = async () => {
        clearErrors();
        if (isNullOrEmpty(categoryName)) {
            handleError({ code: "Category", message: "A category name is required.", details: "" });
            return;
        }

        const input: CategoryInput = { name: categoryName.trim(), parentId };

        setSaving(true);
        try {
            if (isEditing) {
                await onUpdate(editingId!, input);
                onSaved(editingId!);
            } else {
                const created = await onCreate(input);
                onSaved(created.id);
            }
        } catch (e: any) {
            handleError({
                code: "Save failed",
                message: getApiErrorMessage(e, "Unable to save this category."),
                details: "Please try again.",
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
            <Breadcrumbs sx={{ mb: 1 }} aria-label="Category path">
                <HomeOutlinedIcon fontSize="small" sx={{ color: "text.disabled" }} />
                {breadcrumbTrail.map((n) => (
                    <Typography key={n.id} variant="body2" color="text.secondary">
                        {n.name}
                    </Typography>
                ))}
                {!isEditing && (
                    <Typography variant="body2" color="primary">
                        New category
                    </Typography>
                )}
            </Breadcrumbs>

            <Typography variant="h6" gutterBottom>
                {isEditing ? `${isSystem ? "" : "Edit "}"${editingCategory?.name ?? ""}"` : "Create New Category"}
            </Typography>

            {isSystem ? (
                <>
                    <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                        This is a system category, managed centrally and shared across all clients — it can't be
                        edited or removed here. You can still add your own child categories underneath it.
                    </Alert>
                    <Button variant="contained" onClick={() => onAddChild(editingId!)} sx={{ mr: 2 }}>
                        Add child category
                    </Button>
                    <Button variant="text" onClick={onCancel}>
                        Close
                    </Button>
                </>
            ) : (
                <>
                    <ErrorSummary errors={errors} title="Category Detail Errors" />

                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Category Name"
                            value={categoryName}
                            onChange={(e) => setCategoryName(e.target.value)}
                            size="small"
                            autoFocus
                        />

                        <Autocomplete
                            size="small"
                            options={parentOptions}
                            getOptionLabel={(o) => formatIndentedLabel(o.name, o.level).trim()}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={parentOptions.find((o) => o.id === parentId) ?? null}
                            onChange={(_, value) => setParentId(value ? value.id : null)}
                            renderInput={(params) => <TextField {...params} label="Parent Category" placeholder="None (top level)" />}
                        />

                        <Box sx={{ display: "flex", gap: 2, pt: 1 }}>
                            <Button variant="contained" onClick={handleSave} disabled={saving}>
                                {isEditing ? "Update" : "Create"}
                            </Button>
                            {isEditing && (
                                <Button variant="outlined" color="error" onClick={() => onRequestDelete(editingId!)} disabled={saving}>
                                    Remove
                                </Button>
                            )}
                            <Button variant="text" onClick={onCancel} disabled={saving}>
                                Cancel
                            </Button>
                        </Box>
                    </Stack>
                </>
            )}
        </Paper>
    );
}
